import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Axios, { AxiosError } from 'axios';
import { AppDispatch } from '../..';
import CONFIG from '../../../config';
import { ISessionData } from '../../../types';

const isPROD = window.location.host === 'store.ubi.com';

interface IGamePlayed {
  spaceId: string;
  spacePlatformType: string;
  firstPlayed: {
    createdAt: string;
    countryCode: string;
  };
  lastPlayed: {
    updatedAt: string;
    countryCode: string;
  };
  sessionsCount: number;
}

interface IRegistrationStatus {
  UserId: string;
  UserName: string;
  BannedInBeta: false;
  PlayerGroups: PlayerGroup[];
}

interface PlayerGroup {
  PlayerGroupId: null | string;
  Tags: null | string;
  PlatformId: number;
  IsApproved: boolean;
  IsBanned: boolean;
  CountryCode: string;
  PlayerSourceIds: number[];
  DateApprovedAction: null | string;
  PlayerSystemInfo: {
    Components: {
      CTA: string;
    };
  };
}

interface IUserSlice {
  isFetching: boolean;
  gamesPlayed: IGamePlayed[];
  sessionData: ISessionData;
}

const user = createSlice({
  name: 'user',
  initialState: {
    isFetching: false,
    gamesPlayed: [],
    sessionData: {
      authenticated: false,
      ticket: null,
      sessionId: null,
      userId: null
    }
  } as IUserSlice,
  reducers: {
    fetchStarted(state) {
      state.isFetching = true;
    },
    storeSessionData(state, action) {
      state.sessionData = action.payload;
    },
    setGamesPlayed(state, action) {
      state.gamesPlayed = action.payload;
    },

    fetchEnd(state) {
      state.isFetching = false;
    }
  }
});

export const {
  fetchStarted,
  setGamesPlayed,
  fetchEnd,
  storeSessionData
} = user.actions;

export default user.reducer;

export const fetchUserData = (sessionData: ISessionData) => async (
  dispatch: AppDispatch
) => {
  const data =
    sessionData.ticket && sessionData.ticket !== ''
      ? {
          authenticated: true,
          ...sessionData
        }
      : {
          authenticated: false,
          ticket: null,
          sessionId: null,
          userId: null
        };
  dispatch(storeSessionData(data));
  dispatch(fetchStarted());

  if (data.authenticated) {
    const matchedPlayedGames = await fetchGamesPlayed(sessionData);
    dispatch(setGamesPlayed(matchedPlayedGames));
  }

  dispatch(fetchEnd());
};

export const fetchGamesPlayed = async (sessionData: ISessionData) => {
  const gamesPlayedEndpoint = getGamesPlayedEndpoint();
  const headers = {
    'Ubi-AppId': process.env.REACT_APP_APPID,
    Authorization: `ubi_v1 t=${sessionData.ticket}`,
    'Ubi-SessionId': sessionData.sessionId
  };

  try {
    const response = await Axios.get(gamesPlayedEndpoint, {
      headers
    });

    const matchedSpaceIds = response.data.gamesPlayed.filter(
      (game: any) => CONFIG.targetSpaceIds.indexOf(game.spaceId) >= 0
    );

    return matchedSpaceIds;
  } catch (error) {
    console.log('fetch game played error: ', error);
    return [];
  }
};

const getGamesPlayedEndpoint = () => {
  return `https://${
    isPROD ? '' : 'uat-'
  }public-ubiservices.ubi.com/v1/profiles/me/gamesplayed`;
};
